import { AlertTriangle, CheckCircle2, CircleSlash, MinusCircle } from "lucide-react";

import { useAIDiagnostics } from "@/api/queries";
import type { ProviderState } from "@/api/types";
import { Card, CardBody, CardHeader } from "@/components/Card";
import { Skeleton } from "@/components/States";

/**
 * What the AI chain can actually do right now (§13, spec §24).
 *
 * This exists because of one recurring failure: a technician photographs a box,
 * gets an empty proposal, and concludes the feature is bad — when the local
 * model was simply unreachable. From the outside those two are identical, and
 * the difference is an afternoon of someone's time.
 *
 * It shows no key, no endpoint and no host: the server does not send them, and
 * a diagnostics screen ends up in screenshots. Provider name and model version
 * are enough to know what answered.
 */
const ROLE_LABELS: Record<string, string> = {
  barcode: "Codes-barres",
  ocr: "Texte sur étiquette",
  vision_local: "Modèle local",
  vision_cloud: "Repli cloud",
  product_data: "Enrichissement",
};

const STATES: Record<ProviderState, { label: string; icon: typeof CheckCircle2; tone: string }> = {
  ok: { label: "En service", icon: CheckCircle2, tone: "text-success" },
  unreachable: { label: "Injoignable", icon: AlertTriangle, tone: "text-warning" },
  not_configured: { label: "Non configuré", icon: CircleSlash, tone: "text-ink-subtle" },
  disabled: { label: "Désactivé", icon: MinusCircle, tone: "text-ink-subtle" },
};

export function AIStatusPanel() {
  const { data, isLoading, error } = useAIDiagnostics();

  return (
    <Card>
      <CardHeader
        title="État de l'analyse automatique"
        subtitle="Ce qui répond en ce moment. La saisie manuelle ne dépend d'aucune de ces lignes."
      />
      <CardBody className="flex flex-col gap-3">
        {isLoading && <Skeleton className="h-24" />}
        {error && (
          <p className="text-[13px] text-ink-muted">
            Impossible de joindre le diagnostic. L'inventaire fonctionne quand même.
          </p>
        )}
        {data && (
          <>
            <p className="text-[13px] text-ink-muted">{data.summary}</p>
            <ul className="flex flex-col divide-y divide-border/60">
              {data.providers.map((provider) => {
                const state = STATES[provider.state];
                const Icon = state.icon;
                return (
                  <li key={provider.role} className="flex items-start gap-3 py-2">
                    <Icon className={`mt-0.5 size-4 shrink-0 ${state.tone}`} aria-hidden />
                    <div className="min-w-0 flex-1">
                      <p className="text-[13px] text-ink">
                        {ROLE_LABELS[provider.role] ?? provider.role}
                        <span className="ml-2 text-[11px] text-ink-subtle">{state.label}</span>
                      </p>
                      <p className="truncate text-[11px] text-ink-subtle">
                        {provider.provider === "none"
                          ? "aucun fournisseur"
                          : [provider.provider, provider.model_version]
                              .filter(Boolean)
                              .join(" · ")}
                      </p>
                      {provider.detail && (
                        <p className="mt-0.5 text-[11px] text-warning">{provider.detail}</p>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </CardBody>
    </Card>
  );
}
